import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import PageContainer from "../../common_components/PageContainer";
import PagesCommonHeader from "../../common_components/PagesCommonHeader";
import CommonTable from "../../common_components/CommonTable";
import CommonTabs from "../../common_components/CommonTabs";
import ConfirmActionModal from "../../common_components/ConfirmActionModal";
import { TABLE_TYPES } from "../../common_components/tableConstants";
import { SALES_REPORTING_TABS, DEFAULT_VIEW, } from "./utils";
import { getMonthsToShow } from "./Component/shared";
import { useSalesReportingTable } from "./Component/SalesReportingCells";
import SalesReportingHeader from "./Component/SalesReportingHeader";
import DealsSection from "./Component/DealsSection";
import SalesReportingDealDrawer from "./Drawer/SalesReportingDealDrawer";
import LegacyDataEditModal from "./Drawer/LegacyDataEditModal";
import DetailsModal from "./Drawer/DetailsModal";
import { fetchDeals, deleteDeal, fetchReport, syncReport, selectDeals, selectDealsLoading, selectAeReportViews, selectBdrReportViews, selectReportLoading, selectReportLastUpdated, selectReportSyncing, } from "../../redux/salesReportingSlice";
import { selectCanAddDeal, selectCanEditLegacyData, selectCanViewAeReport, selectCanViewBdrReport, selectIsSuperAdmin, } from "../../redux/authSlice";
import { useLayout } from "../../contexts/LayoutContext";
import "../../styles/SalesReporting.css";
const SalesReportingMain = () => {
    const dispatch = useDispatch();
    const { setIsSidebarExpanded } = useLayout();
    // The 4 deal-tracker tables are grouped server-side (single source of truth in
    // the controller) and consumed here as-is.
    const deals = useSelector(selectDeals);
    const dealsLoading = useSelector(selectDealsLoading);
    const aeViews = useSelector(selectAeReportViews);
    const bdrViews = useSelector(selectBdrReportViews);
    const reportLoading = useSelector(selectReportLoading);
    const reportLastUpdated = useSelector(selectReportLastUpdated);
    const reportSyncing = useSelector(selectReportSyncing);
    // Sales Reporting module actions: "Add Deal" → manage deals, "Legacy Data Edit" → edit BDR past-month (legacy) data cells.
    const canAddDeal = useSelector(selectCanAddDeal);
    const canEditLegacyData = useSelector(selectCanEditLegacyData);
    // "AE"/"BDR" actions gate which report tabs are shown. Super admins see both.
    const isSuperAdmin = useSelector(selectIsSuperAdmin);
    const canViewAe = useSelector(selectCanViewAeReport);
    const canViewBdr = useSelector(selectCanViewBdrReport);
    const visibleReportTabs = useMemo(() => SALES_REPORTING_TABS.filter((t) => isSuperAdmin || (t.value === "AE" ? canViewAe : canViewBdr)), [isSuperAdmin, canViewAe, canViewBdr]);
    // Collapse the sidebar submenu on visit (same behavior as Email Configuration).
    useEffect(() => {
        setIsSidebarExpanded(false);
    }, [setIsSidebarExpanded]);
    // Allow the report emails to deep-link to a tab via ?tab=ae|bdr. Parse the param
    // directly (permissions load async, so visibleReportTabs is empty at mount); the
    // clamp effect below demotes to an allowed tab only if the user can't view this one.
    const initialTab = useMemo(() => (new URLSearchParams(window.location.search).get("tab")?.toUpperCase() === "BDR" ? "BDR" : "AE"), []);
    const [activeTab, setActiveTab] = useState(initialTab);
    // Keep the active tab within the set the user is allowed to see (e.g. permissions change).
    useEffect(() => {
        if (visibleReportTabs.length && !visibleReportTabs.some((t) => t.value === activeTab)) {
            setActiveTab(visibleReportTabs[0].value);
        }
    }, [visibleReportTabs, activeTab]);
    // The report (header + matrix) shows only for a tab the user is allowed to view (covers
    // super admin); hiding a tab or forcing ?tab=ae|bdr via the URL can't leak the report.
    const canSeeActiveReport = visibleReportTabs.some((t) => t.value === activeTab);
    // Deal tracker tables belong to AE: shown on the AE tab, hidden on the BDR tab. A user with
    // no report access (no AE/BDR) sees the deals directly. Only the Add Deal button is gated.
    const canSeeDeals = !canSeeActiveReport || activeTab === "AE";
    const [view, setView] = useState(DEFAULT_VIEW);
    const [year, setYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerMode, setDrawerMode] = useState("add");
    const [selectedDeal, setSelectedDeal] = useState(null);
    const [editLegacyData, setEditLegacyData] = useState(null);
    const [detailsCtx, setDetailsCtx] = useState(null);
    const [deleteState, setDeleteState] = useState({
        open: false,
        record: null,
    });
    useEffect(() => {
        dispatch(fetchDeals());
    }, [dispatch]);
    // Report matrix — refetch when the active tab or year changes (DB-only fetch).
    // Skip when the user isn't allowed to view the active tab (no data leak).
    useEffect(() => {
        if (!canSeeActiveReport)
            return;
        dispatch(fetchReport({ type: activeTab === "BDR" ? "bdr" : "ae", year }));
    }, [dispatch, activeTab, year, canSeeActiveReport]);
    // "By Channel" is AE-only — on BDR display Top Line without losing the selection (restored on return to AE).
    const effectiveView = activeTab === "BDR" && view === "channel" ? DEFAULT_VIEW : view;
    const handleMonthChange = (v) => {
        if (v === "ALL_TIME") {
            setSelectedMonth(null);
            return;
        }
        if (!v || !Number.isInteger(v.year) || !Number.isInteger(v.month))
            return;
        if (v.year !== year)
            setYear(v.year);
        setSelectedMonth(v);
    };
    const handleReset = () => {
        setYear(new Date().getFullYear());
        setSelectedMonth(null);
        setView(DEFAULT_VIEW);
    };
    const handleSync = () => {
        if (reportSyncing)
            return;
        // Selected month syncs that month; otherwise the refresh syncs only the current month (AE + BDR), then refreshes.
        dispatch(syncReport({
            year,
            month: selectedMonth ? selectedMonth.month : undefined,
            type: activeTab === "BDR" ? "bdr" : "ae",
        }));
    };
    const handleAddDeal = () => {
        setDrawerMode("add");
        setSelectedDeal(null);
        setDrawerOpen(true);
    };
    const handleEditDeal = (record) => {
        setDrawerMode("edit");
        setSelectedDeal(record);
        setDrawerOpen(true);
    };
    const months = useMemo(() => getMonthsToShow(year, selectedMonth ? selectedMonth.month : null), [year, selectedMonth]);
    const viewData = (activeTab === "BDR" ? bdrViews?.[effectiveView] : aeViews?.[effectiveView]) || null;
    const { columns, tableRows, renderCell } = useSalesReportingTable(activeTab, effectiveView, year, months, viewData, selectedMonth === null, // YTD only in the "All time" (multi-month) view
    // BDR past-month cell opens the Legacy Data Edit popup — only when the user has the "Legacy Data Edit" action.
    canEditLegacyData ? (ctx) => setEditLegacyData(ctx) : undefined, (ctx) => setDetailsCtx(ctx) // BDR current-month user cell: details popup (read-only, always allowed)
    );
    return (_jsxs(PageContainer, { children: [_jsxs(Box, { className: "sr-page-wrapper", children: [canSeeActiveReport && (_jsxs(_Fragment, { children: [_jsx(SalesReportingHeader, { activeTab: activeTab, lastUpdated: reportLastUpdated, syncing: reportSyncing, view: effectiveView, onViewChange: setView, selectedMonth: selectedMonth, onMonthChange: handleMonthChange, onAllTimeYear: setYear, year: year, onRefresh: handleSync, onReset: handleReset }), _jsxs(Paper, { elevation: 0, className: "sr-card", children: [_jsx(PagesCommonHeader, { height: "48px", lastUpdatedSection: _jsx(CommonTabs, { selectedValue: activeTab, onChange: setActiveTab, tabs: visibleReportTabs }) }), _jsx(Box, { className: "sr-table-wrapper", children: _jsx(Box, { className: "sr-table-box sr-report-box", children: _jsx(CommonTable, { columns: columns, rows: tableRows, renderCell: renderCell, type: TABLE_TYPES.GROUPED, tableHeadClassName: "reusable-table-header-label all-Font-Sizes", stickyHeader: true, maxHeight: "64vh", expandTrigger: "none", isLoading: reportLoading, emptyMessage: "No report data available.", rowProps: (row) => (row.is_total ? { "data-total-row": "true" } : {}) }) }) }), _jsx(Box, { className: "sr-pagination-wrapper", children: _jsx(Typography, { className: "sr-entries-text all-Font-Sizes", sx: { fontSize: "14px !important", fontWeight: 500 }, children: tableRows.length > 0
                                                ? `Showing 1 to ${tableRows.length} of ${tableRows.length} entries`
                                                : "Showing 0 to 0 of 0 entries" }) })] })] })), canSeeDeals && (_jsx(DealsSection, { deals: deals, loading: dealsLoading, canManage: canAddDeal, onAdd: handleAddDeal, onEdit: handleEditDeal, onDelete: (record) => setDeleteState({ open: true, record }) }))] }), _jsx(SalesReportingDealDrawer, { open: drawerOpen, onClose: () => setDrawerOpen(false), mode: drawerMode, record: selectedDeal }), _jsx(LegacyDataEditModal, { open: !!editLegacyData, context: editLegacyData, onClose: () => setEditLegacyData(null) }), _jsx(DetailsModal, { open: !!detailsCtx, context: detailsCtx, onClose: () => setDetailsCtx(null) }), _jsx(ConfirmActionModal, { open: deleteState.open, title: "Delete Deal", description: `Are you sure you want to delete "${deleteState.record?.deal_name || ""}"? This action cannot be undone.`, confirmText: "Delete", onConfirm: () => {
                    if (deleteState.record?._id) {
                        dispatch(deleteDeal(deleteState.record._id));
                    }
                    setDeleteState({ open: false, record: null });
                }, onClose: () => setDeleteState({ open: false, record: null }) })] }));
};
export default SalesReportingMain;
